<template>
  <div class="mf-card-preview" data-testid="mfcard">
    <!-- ══ TAX_FUND_FACTSHEET (type8) ═════════════════════════════════════════════════════
         One fund's identity card: who it is, what a unit costs today, what it promises to
         do with the money, how it has behaved, and what it holds. Every block below is
         `v-if`-ed on its own content, so a half-written payload draws the half that exists
         instead of a frame full of blanks. -->
    <div
      v-if="kind === 'factsheet'"
      class="mf-frame"
      :style="frameStyle"
      data-testid="mfcard-factsheet"
    >
      <!-- head: logo, ticker, full name -->
      <div class="mf-head">
        <img
          v-if="iconUrl && !iconBroken"
          :src="iconUrl"
          class="mf-icon"
          alt=""
          data-testid="mfcard-icon"
          @error="iconBroken = true"
        />
        <div class="mf-head-text">
          <div v-if="text(cardData.symbol)" class="mf-symbol" :style="ts(cardData, 'symbol')">
            {{ text(cardData.symbol) }}
          </div>
          <div v-if="text(cardData.fundName)" class="mf-fund-name" :style="ts(cardData, 'fundName')">
            {{ text(cardData.fundName) }}
          </div>
        </div>
      </div>

      <!-- NAV: the one number the customer came for, plus the day's move -->
      <div v-if="nav" class="mf-nav" data-testid="mfcard-nav">
        <div class="mf-nav-line">
          <span v-if="text(nav.label)" class="mf-nav-label" :style="ts(nav, 'label')">
            {{ text(nav.label) }}
          </span>
          <span class="mf-nav-value" :style="ts(nav, 'value')">{{ navValue }}</span>
          <span v-if="text(nav.unit)" class="mf-nav-unit" :style="ts(nav, 'value')">
            {{ text(nav.unit) }}
          </span>
          <span v-if="navChange" class="mf-nav-change" :class="navChange.cls">
            {{ navChange.text }}
          </span>
        </div>
        <div v-if="text(nav.caption)" class="mf-caption" :style="ts(nav, 'caption')">
          {{ text(nav.caption) }}
        </div>
      </div>

      <!-- policy: prose first, then the exceptions as bullets -->
      <div v-if="text(cardData.policyTitle)" class="mf-section-title" :style="ts(cardData, 'policyTitle')">
        {{ text(cardData.policyTitle) }}
      </div>
      <p
        v-for="(para, i) in policyParagraphs"
        :key="'pp' + i"
        class="mf-para"
        :style="ts(cardData, 'policyParagraphs')"
      >{{ para }}</p>
      <ul v-if="policyBullets.length" class="mf-bullets" :style="ts(cardData, 'policyBullets')">
        <li v-for="(b, i) in policyBullets" :key="'pb' + i">{{ b }}</li>
      </ul>

      <!-- stats: label left, figure right, coloured by the MEANING the payload declared -->
      <div v-if="stats.length" class="mf-rows" data-testid="mfcard-stats">
        <div v-for="(s, i) in stats" :key="'st' + i" class="mf-row">
          <span class="mf-row-label" :style="ts(s, 'label')">{{ text(s.label) }}</span>
          <span
            class="mf-row-value"
            :class="toneClass(s.tone)"
            :style="valueStyle(s, 'value', s.tone)"
          >{{ text(s.value) || EM_DASH }}</span>
        </div>
      </div>
      <div v-if="text(cardData.statsCaption)" class="mf-caption" :style="ts(cardData, 'statsCaption')">
        {{ text(cardData.statsCaption) }}
      </div>

      <!-- holdings -->
      <div
        v-if="text(cardData.holdingsTitle)"
        class="mf-section-title"
        :style="ts(cardData, 'holdingsTitle')"
      >{{ text(cardData.holdingsTitle) }}</div>
      <div v-if="holdings.length" class="mf-rows" data-testid="mfcard-holdings">
        <div v-for="(h, i) in holdings" :key="'hd' + i" class="mf-row">
          <span class="mf-row-label" :style="ts(h, 'name')">{{ text(h.name) || EM_DASH }}</span>
          <span class="mf-row-value" :style="ts(h, 'percent')">{{ percent(h.percent) }}</span>
        </div>
      </div>
      <div v-if="text(cardData.holdingsCaption)" class="mf-caption" :style="ts(cardData, 'holdingsCaption')">
        {{ text(cardData.holdingsCaption) }}
      </div>

      <div v-if="text(cardData.disclaimer)" class="mf-disclaimer" :style="ts(cardData, 'disclaimer')">
        {{ text(cardData.disclaimer) }}
      </div>

      <div v-if="footer" class="mf-footer" data-testid="mfcard-footer">
        <span v-if="text(footer.label)" class="mf-footer-label" :style="ts(footer, 'label')">{{ text(footer.label) }}</span>
        <a
          v-if="footerUrl"
          class="mf-link"
          :href="footerUrl"
          target="_blank"
          rel="noopener"
          :style="ts(footer, 'linkLabel')"
        >{{ text(footer.linkLabel) || footerUrl }}</a>
        <span v-else-if="text(footer.linkLabel)" :style="ts(footer, 'linkLabel')">
          {{ text(footer.linkLabel) }}
        </span>
      </div>
    </div>

    <!-- ══ TAX_FUND_COMPARISON (type9) + TAX_FUND_PERFORMANCE (type10) ════════════════════
         ONE branch on purpose: both cards are "a title over a grid whose first column is a
         row header", and the only differences are which keys carry the header label and the
         column list (see `tableModel`) and which captions sit under the grid. Two copies of
         this markup would be two places for the frozen column to drift apart. -->
    <div
      v-else-if="tableModel"
      class="mf-frame"
      :style="frameStyle"
      :data-testid="kind === 'comparison' ? 'mfcard-comparison' : 'mfcard-performance'"
    >
      <div v-if="text(cardData.title)" class="mf-title" :style="ts(cardData, 'title')">
        {{ text(cardData.title) }}
      </div>

      <div class="mf-table-wrap" :style="tableModel.wrapStyle" data-testid="mfcard-table">
        <table class="mf-table">
          <thead>
            <tr>
              <th class="mf-th mf-freeze" :style="tableModel.headStyle">
                {{ tableModel.headLabel }}
              </th>
              <th v-for="(c, i) in tableModel.columns" :key="'c' + i" class="mf-th" :style="c.style">
                {{ c.text }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(r, ri) in tableModel.rows" :key="'r' + ri">
              <th class="mf-td mf-freeze mf-rowhead" :style="r.headStyle">{{ r.headText }}</th>
              <td
                v-for="(c, ci) in r.cells"
                :key="'c' + ci"
                class="mf-td"
                :class="c.toneClass"
                :style="c.style"
              >{{ c.text }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-if="text(cardData.scrollHint)" class="mf-caption" :style="ts(cardData, 'scrollHint')">
        {{ text(cardData.scrollHint) }}
      </div>
      <div v-if="text(cardData.caption)" class="mf-caption" :style="ts(cardData, 'caption')">
        {{ text(cardData.caption) }}
      </div>
      <div v-if="text(cardData.disclaimer)" class="mf-disclaimer" :style="ts(cardData, 'disclaimer')">
        {{ text(cardData.disclaimer) }}
      </div>

      <div v-if="footer && (text(footer.label) || footerLinks.length)" class="mf-footer" data-testid="mfcard-footer">
        <span v-if="text(footer.label)" class="mf-footer-label" :style="ts(footer, 'label')">{{ text(footer.label) }}</span>
        <span v-for="(l, i) in footerLinks" :key="'fl' + i" class="mf-footer-link">
          <a
            v-if="l.url"
            class="mf-link"
            :href="l.url"
            target="_blank"
            rel="noopener"
            :style="l.style"
          >{{ l.text }}</a>
          <span v-else :style="l.style">{{ l.text }}</span>
          <span v-if="l.sep" class="mf-sep">{{ l.sep }}</span>
        </span>
      </div>
    </div>

    <!-- ══ MF (type14) — the fund picker ══════════════════════════════════════════════════
         The only card here the customer ANSWERS rather than reads: the search matched more
         than one fund, so the phone lists what it found and sends the choice back as an
         ordinary chat message. None of the controls below are live — the phone owns the real
         ones — and drawing them is not the point either. The point is the SENTENCE at the
         bottom: `prefix` + `join` are legible only as the string they concatenate into. -->
    <div v-else-if="picker" class="mf-frame" :style="picker.frameStyle" data-testid="mfcard-picker">
      <div class="mf-picker-mode" data-testid="mfcard-picker-mode">
        <v-icon
          size="16"
          class="mf-picker-mode-icon"
          :icon="picker.compare ? 'mdi-format-list-checks' : 'mdi-gesture-tap'"
        />
        <span>{{ picker.modeLabel }}</span>
      </div>

      <!-- the options, as the phone draws them: ONE FULL-WIDTH PILL PER FUND, stacked. Not a radio
           list and not a checkbox list — the two modes look almost identical on the phone and differ
           only in the ► on each pill (single-select: tapping one sends immediately) and in whether a
           submit pill closes the stack (compare: tick several, then send).
           `list` empty ⇒ say so: an empty frame reads as "the card renders", and a picker with
           nothing to pick is the one failure this card can have. -->
      <div
        v-if="picker.items.length"
        class="mf-picker-list"
        :style="picker.listStyle"
        data-testid="mfcard-picker-list"
      >
        <div v-for="(item, i) in picker.items" :key="'pk' + i" class="mf-pill" :style="picker.itemStyle">
          <span class="mf-pill-text">{{ item }}</span>
          <span v-if="!picker.compare" class="mf-pill-arrow" :style="picker.arrowStyle" aria-hidden="true">►</span>
        </div>
      </div>
      <v-alert
        v-else
        type="warning"
        variant="tonal"
        density="compact"
        data-testid="mfcard-picker-empty"
      >
        <div class="text-caption">list ว่าง — เทิร์นที่ออกการ์ดใบนี้จะไม่มีอะไรให้ลูกค้าเลือกเลย</div>
      </v-alert>

      <!-- the submit pill: the same shape and width as a fund pill, darker, white text, white ►.
           ONLY on the compare card — drawing it under a single-select picker would promise a step
           the customer never gets. An empty caption shows as an em dash inside an otherwise
           convincing button, which is exactly the mistake worth seeing. -->
      <div
        v-if="picker.compare"
        class="mf-pill mf-pill-submit"
        :style="picker.itemShapeStyle"
        data-testid="mfcard-picker-submit"
      >
        <span class="mf-pill-text">{{ picker.submitText || EM_DASH }}</span>
        <span class="mf-pill-arrow" aria-hidden="true">►</span>
      </div>

      <!-- Placed UNDER the whole stack, never between the funds and the submit pill: those two are
           one object on the phone and a caption wedged between them reads as a separator. -->
      <div v-if="picker.repeatFrom" class="mf-caption" data-testid="mfcard-picker-source">
        รายชื่อจริงมาจาก <code>{{ picker.repeatFrom }}</code> ตอนรันจริง (มากสุดเท่า Top N ของโหนด) —
        สามใบข้างบนเป็นแค่ตัวอย่างให้เห็นรูปทรง
      </div>

      <!-- 🔴 `white-space: pre-wrap` on the echo, so the trailing space in `prefix` is a gap
           the admin can SEE instead of a character HTML collapses away. -->
      <div class="mf-picker-echo" data-testid="mfcard-picker-echo">
        <div class="mf-picker-echo-title">ข้อความที่ลูกค้าจะส่งกลับ</div>

        <!-- the seats the sentence is built from, verbatim. `prefix` never appears on the card
             itself — it is only ever the head of the message that goes BACK — so it is shown
             here as a value, not as a line of card copy. -->
        <div v-for="seat in picker.seats" :key="seat.key" class="mf-picker-seat">
          <span class="mf-picker-seat-key">{{ seat.key }}</span>
          <span class="mf-picker-echo-line">{{ seat.value === '' ? EM_DASH : seat.value }}</span>
        </div>

        <div class="mf-picker-echo-label">ผลลัพธ์</div>
        <div class="mf-picker-echo-line">{{ picker.echo || EM_DASH }}</div>
      </div>

      <v-alert
        v-if="picker.warnings.length"
        type="warning"
        variant="tonal"
        density="compact"
        class="mt-3"
        data-testid="mfcard-picker-warnings"
      >
        <div v-for="(w, i) in picker.warnings" :key="'pw' + i" class="text-caption">{{ w }}</div>
      </v-alert>
    </div>

    <!-- ══ TAX_FUND_COMPARISON_V2 (type15) ═══════════════════════════════════════════════════
         Its own component: four tabs, period chips and a chart keep state this file has no use for. -->
    <div v-else-if="kind === 'comparisonV2'" class="mf-v2" data-testid="mfcard-comparison-v2-host">
      <MutualFundComparisonV2Preview :card="cardObj" />
    </div>

    <!-- ══ the seven card types the tax renderer already owns ═════════════════════════════
         Handed over whole. `preview` (not `interactive`) is what the card authoring surfaces
         pass: the CTAs draw but stay dead, while the simulation's sliders still move — see
         `TaxSummaryCardV2`'s prop docs. -->
    <div v-else-if="kind === 'delegate'" class="mf-delegate" data-testid="mfcard-delegate">
      <TaxSummaryCardV2 :card="card" :raw="raw" preview />
    </div>

    <!-- ══ nothing typed yet ══════════════════════════════════════════════════════════════
         NOT a warning. An empty JSON box is the state every new row starts in, and shouting
         "unsupported card" at an admin who has typed nothing is noise, not help. -->
    <div v-else-if="kind === 'empty'" class="mf-empty" data-testid="mfcard-empty">
      ยังไม่มีการ์ดให้แสดง — วาง JSON ของการ์ดในกล่องทางซ้าย
    </div>

    <!-- ══ a document this component cannot draw ══════════════════════════════════════════
         The honest answer, plus the payload itself so the admin can see what they typed.
         Never a blank panel: an empty preview reads as "the card renders to nothing", which
         is a different (and wrong) claim. -->
    <div v-else class="mf-unsupported" data-testid="mfcard-unsupported">
      <v-alert
        type="warning"
        variant="tonal"
        density="compact"
        class="mb-2"
        data-testid="mfcard-unsupported-alert"
      >{{ unsupportedMessage }}</v-alert>
      <pre v-if="rawDisplay" class="mf-raw" data-testid="mfcard-raw">{{ rawDisplay }}</pre>
    </div>
  </div>
</template>

<script>
/**
 * MutualFundCardPreview — draws a mutual-fund tax card straight from the JSON an admin is
 * typing, with no server in the loop.
 *
 * It is the right-hand column of `MutualFundCardConfigDialog`: the admin edits a card's
 * payload on the left, this draws it on the right, per keystroke. Nothing here fetches,
 * posts, or normalises the document — `utils/mutualFundCardConfig.parseCardConfig` has
 * already turned the textarea's text into an object (or into `null` plus a message), and
 * this component's whole job is to answer "what does that object look like".
 *
 * ── The four rules this file is built on ────────────────────────────────────────────────
 *
 * 1. IT NEVER THROWS, AND A HALF-WRITTEN CARD DRAWS ITS HALF. The input is a document the
 *    admin is in the middle of typing, so "the key is missing", "the list is a string" and
 *    "this row has four cells where the header promised five" are the NORMAL states, not
 *    error states. Every list goes through `asList`, every nested object through `asObj`,
 *    every scalar through `asText`; a row short of cells is padded with blanks and a row
 *    with too many widens the grid (see `tableModel`). One bad key must never cost the
 *    author the rest of the card — a white panel tells them nothing about what they broke.
 *
 * 2. STYLE COMES FROM THE PAYLOAD; THE STYLESHEET IS ONLY THE FALLBACK. Every `*Color`,
 *    `*FontSize`, `mainFrameColor` and `rowFrameColor` in the document lands in an inline
 *    style through the SHARED helpers in `utils/taxCardFormat` (`textStyle` / `fillStyle` /
 *    `mergeStyles`) — the same readers `TaxSummaryCardV2` uses, so `#AARRGGBB` is reordered
 *    to CSS's `#RRGGBBAA` here exactly as it is there. A card that authored nothing renders
 *    in this file's own palette rather than blank or black.
 *
 * 3. 🔴 `tone` BEATS `valueColor`, WHICH IS THE ONE PLACE THIS DIFFERS FROM `TaxSummaryCardV2`.
 *    There, an explicit colour hint always wins over the tone that row also declared. Here
 *    it does not, and deliberately: the BACKEND (`MutualFundArtifactProjection.Stat` /
 *    `.PerformanceRow`) emits `tone` and NO colour at all — it is derived from the figure's
 *    sign and is the only signal that "+6.82%" is a gain — while the hand-authored payloads
 *    carry one blanket `valueColor` on every row, gains and losses alike. Letting that grey
 *    win would silently delete the only thing `tone` exists to say. So POSITIVE/NEGATIVE
 *    paint the figure (`valueStyle` drops the authored `color` and lets the class through);
 *    `DEFAULT` and anything unrecognised keep the author's colour and add no class.
 *
 * 4. ONLY http(s) AND mailto LINKS BECOME `<a href>`. A card payload is admin-authored, but
 *    it is still a document travelling through a database and an API, and `javascript:` in
 *    an `href` is a script that runs on click. Anything else prints as plain text — the
 *    admin still sees the label, they just cannot click a URL this component would not
 *    stand behind. Every link that IS drawn gets `target="_blank" rel="noopener"`, because
 *    the preview lives inside a dialog that must not be navigated away from.
 *
 * ── Drawn elsewhere ─────────────────────────────────────────────────────────────────────
 * • `TAX_FUND_COMPARISON_V2` (type15) goes to `MutualFundComparisonV2Preview` with the whole
 *   envelope, and the seven tax card types to `TaxSummaryCardV2`.
 *
 * ── What this component does NOT draw, and why ──────────────────────────────────────────
 * • The NESTED `{ factsheet, performance }` shape of type10 that `MutualFundArtifactProjection
 *   .DemoPerformance` still produces. The flat shape in `tasks/…/samples/TAX_FUND_PERFORMANCE
 *   .json` is the agreed contract; supporting both would make "which one is right?" a
 *   question this preview answers differently from the phone.
 * • A performance/comparison cell's `note` (the backend writes "annualized" on some rows).
 *   It is absent from the agreed sample and has no agreed position in the grid, so inventing
 *   one here would preview a layout the phone does not have.
 */
import TaxSummaryCardV2 from './TaxSummaryCardV2.vue';
import MutualFundComparisonV2Preview from './MutualFundComparisonV2Preview.vue';
import { detectCardType } from '../utils/mutualFundCardConfig';
import { EM_DASH, textStyle, fillStyle, mergeStyles } from '../utils/taxCardFormat';
import {
  pickerArrowStyle,
  pickerFrameStyle,
  pickerItemShapeStyle,
  pickerItemStyle,
  pickerListStyle,
} from '../utils/mutualFundPickerStyle';

/**
 * The card types `TaxSummaryCardV2` owns — mirrors its own `CARD_TYPES` map, which is a
 * module-private const inside a 5,000-line SFC and cannot be imported.
 *
 * 🔴 MIRROR, NOT GUESS. A type listed here that the renderer does not actually know falls
 * into its `v-else` and draws a plausible-looking EMPTY card; a type it knows but that is
 * missing here lands in this file's honest "unsupported" block instead. The first failure
 * is the dangerous one, so when in doubt leave a type OUT.
 */
const DELEGATED_TYPES = [
  'TAX_INCOME_BREAKDOWN',
  'TAX_INCOME_EXPENSE',
  'TAX_ALLOWANCE',
  'TAX_AMOUNT_FORM',
  'TAX_SUMMARY',
  'TAX_FILING_COMPARE',
  'TAX_SIMULATION',
];

/** `tone` → the class that paints the figure. `DEFAULT`/absent/unknown ⇒ no class (rule 3). */
const TONE_CLASS = { POSITIVE: 'mf-pos', NEGATIVE: 'mf-neg' };

/**
 * The three languages a card seat has to carry, in the order the phone falls back through.
 *
 * 🔴 A bag missing ONE of them drops the WHOLE CARD back to Thai for that customer — the
 * normaliser keeps only complete bags — so "which language is missing" is worth saying out
 * loud on the authoring screen, where it is otherwise invisible in a wall of JSON.
 */
const CARD_LANGS = ['th', 'en', 'zh'];

/**
 * What the picker's option list looks like while the admin is authoring the TEMPLATE.
 *
 * The real codes arrive at run time from `mf.funds`; a template can only show the SHAPE. The
 * guillemets are there so nobody reads these as fund codes we invented.
 */
const PICKER_PLACEHOLDERS = ['«กองที่ 1»', '«กองที่ 2»', '«กองที่ 3»'];

/**
 * One authored seat that is either a plain string or a `{ th, en, zh }` language bag.
 *
 * 🔴 NOTHING IS TRIMMED, unlike {@link asText}. `prefix` ends in a SPACE on purpose — the
 * phone sends `prefix + code` — and a preview that trimmed it would draw a tidy sentence
 * while the customer receives one with the code welded onto the last word.
 *
 * @returns {{text: string, bag: boolean, missing: string[]}} `missing` is the languages a bag
 *   left blank; always empty for a plain string, which is one language by definition.
 */
function langSeat(value) {
  if (typeof value === 'string') return { text: value, bag: false, missing: [] };
  const bag = asObj(value);
  if (!bag) return { text: '', bag: false, missing: [] };
  const filled = (lang) => typeof bag[lang] === 'string' && bag[lang].trim() !== '';
  const first = CARD_LANGS.find(filled);
  return {
    text: first ? bag[first] : '',
    bag: true,
    missing: CARD_LANGS.filter((lang) => !filled(lang)),
  };
}

/** The only URL schemes this component will put behind a click (rule 4). */
const SAFE_URL = /^(https?:\/\/|mailto:)/i;

/** Truthy object that is not an array — the only thing worth reading keys off. */
function asObj(v) {
  return v && typeof v === 'object' && !Array.isArray(v) ? v : null;
}

/** A real array, or an empty one. Never null, so callers can `.map` blind. */
function asList(v) {
  return Array.isArray(v) ? v : [];
}

/**
 * One scalar as display text: `''` for null/undefined/objects/arrays, the trimmed string
 * form otherwise.
 *
 * 🔴 `0` and `false` survive as '0'/'false' — they are DATA. Objects answer '' rather than
 * '[object Object]', because a payload whose `label` is an object is a bug the author needs
 * to see as a missing label, not as noise printed into the card.
 */
function asText(v) {
  if (v === null || v === undefined) return '';
  if (typeof v === 'object') return '';
  return String(v).trim();
}

/**
 * How many decimals a number was AUTHORED with, capped at 6.
 *
 * 🔴 The reason this file does not use `taxCardFormat.formatAmount`: that one is a MONEY
 * formatter and always prints exactly two decimals, which would round a NAV of 10.4521 —
 * a four-decimal figure by convention — into 10.45 and quietly change the number the card
 * is quoting.
 */
function decimalsOf(n) {
  const s = String(n);
  const dot = s.indexOf('.');
  if (dot < 0) return 0;
  const d = s.length - dot - 1;
  return d > 6 ? 6 : d;
}

/**
 * A number as the card prints it: grouped, keeping the precision it was written with. `null` when it is not a number.
 *
 * 🔴 For TEXT the precision is read off the text, not off the parsed number: a template can
 * format on the server (`{{ mf.fund.return_1day | +0.00 }}` ⇒ "-1.10"), and `String(-1.1)` would
 * drop the zero the author asked for — the builder would show "-1.1" while the app gets "-1.10".
 */
function fundNumber(v) {
  let n = NaN;
  let d = null;
  if (typeof v === 'number') n = v;
  else if (typeof v === 'string' && v.trim() !== '') {
    const s = v.trim();
    n = Number(s);
    const plain = /^[+-]?\d+(?:\.(\d+))?$/.exec(s);
    if (plain) d = Math.min(plain[1] ? plain[1].length : 0, 6);
  }
  if (!Number.isFinite(n)) return null;
  if (d === null) d = decimalsOf(n);
  return n.toLocaleString('th-TH', { minimumFractionDigits: d, maximumFractionDigits: d });
}

export default {
  name: 'MutualFundCardPreview',
  components: { TaxSummaryCardV2, MutualFundComparisonV2Preview },
  props: {
    /**
     * The card envelope `{ type, version, refId, data }`, already parsed.
     *
     * 🔴 `default: null` and NOT `required`: the box is empty most of the time an admin is
     * working, and the parser answers `null` for both "nothing typed" and "does not parse".
     * The component tells those two apart by looking at `raw` (see `kind`).
     */
    card: { type: Object, default: null },
    /** The JSON text exactly as typed — shown verbatim when the card cannot be drawn. */
    raw: { type: String, default: '' },
  },
  data() {
    return {
      /**
       * Did `iconUrl` fail to load? A dead `<img>` prints a broken-image glyph where a fund
       * logo should be, which reads as "this card is broken" rather than "this URL is".
       */
      iconBroken: false,
    };
  },
  computed: {
    /** The em dash, exposed so the template can print it for an unreadable figure. */
    EM_DASH: () => EM_DASH,

    /** The envelope, or null when the prop is absent/not an object. */
    cardObj() {
      return asObj(this.card);
    },

    /** `card.data`, or `{}` — an envelope with no payload still draws its frame. */
    cardData() {
      return asObj(this.cardObj && this.cardObj.data) || {};
    },

    /** The JSON text, trimmed. '' ⇒ the admin has typed nothing at all. */
    rawText() {
      return typeof this.raw === 'string' ? this.raw.trim() : '';
    },

    /**
     * WHICH branch draws this card. The single decision this component makes.
     *
     * 'empty' only when there is neither a card NOR any text: a card that is `null` while
     * `raw` holds something is a document that failed to parse, and that deserves the
     * warning plus the text, not a friendly placeholder.
     */
    kind() {
      if (!this.cardObj) return this.rawText ? 'unsupported' : 'empty';
      const type = detectCardType(this.cardObj);
      if (type === 'TAX_FUND_FACTSHEET') return 'factsheet';
      if (type === 'TAX_FUND_COMPARISON') return 'comparison';
      if (type === 'TAX_FUND_COMPARISON_V2') return 'comparisonV2';
      if (type === 'TAX_FUND_PERFORMANCE') return 'performance';
      if (type === 'MF') return 'picker';
      if (DELEGATED_TYPES.indexOf(type) >= 0) return 'delegate';
      return 'unsupported';
    },

    /** The card-wide background — `mainFrameColor`, or the stylesheet's own white. */
    frameStyle() {
      return fillStyle(this.cardData, 'mainFrameColor');
    },

    /** Why this card is not being drawn — three different problems, three different sentences. */
    unsupportedMessage() {
      if (!this.cardObj) return 'อ่านการ์ดจาก JSON นี้ไม่ได้ — ตรวจข้อความในกล่องทางซ้าย';
      const type = detectCardType(this.cardObj);
      if (!type) return 'การ์ดนี้ไม่ได้ระบุ "type" จึงยังไม่รู้ว่าต้องวาดด้วย renderer ตัวไหน';
      return 'ยังไม่รองรับการ์ดชนิดนี้: ' + type;
    },

    /**
     * The document to print under the warning.
     *
     * Falls back to re-serialising `card` when the caller passed an object without its text
     * (the dialog always passes both, but a card handed over from elsewhere may not) — a
     * warning with nothing under it tells the reader nothing about what was rejected.
     */
    rawDisplay() {
      if (this.rawText) return this.raw;
      if (!this.cardObj) return '';
      try {
        return JSON.stringify(this.cardObj, null, 2);
      } catch (_err) {
        return '';
      }
    },

    // ── factsheet ──────────────────────────────────────────────────────────────────────
    iconUrl() {
      const url = asText(this.cardData.iconUrl);
      return SAFE_URL.test(url) ? url : '';
    },
    nav() {
      return asObj(this.cardData.nav);
    },
    /** The NAV figure: formatted when it is a number, printed verbatim when it is text. */
    navValue() {
      if (!this.nav) return EM_DASH;
      const formatted = fundNumber(this.nav.value);
      if (formatted !== null) return formatted;
      return asText(this.nav.value) || EM_DASH;
    },
    /**
     * The day's move, with its sign spelled out and its direction coloured.
     *
     * 🔴 The '+' is a GLYPH the card has to add: `toLocaleString` prints a minus for negative
     * numbers and nothing for positive ones, so an unsigned "0.0842" would read as the NAV's
     * own second line rather than as a gain. `0` gets no sign and no colour — it has no
     * direction to claim.
     *
     * 🔴 `changeLabel` is the TEXT, `change` is the DIRECTION. The app draws its up/down triangle
     * off `change` (a bare number — that contract does not move) and prints `changeLabel` as the
     * author formatted it, e.g. "-1.09 (-1.10%)" — no '+' is added, because the author chose the
     * sign. The builder has no triangle, so the direction shows as the colour, read off `change`.
     * A card without `changeLabel` falls back to `change` as before.
     */
    navChange() {
      if (!this.nav) return null;
      const value = this.nav.change;
      const label = asText(this.nav.changeLabel);
      const formatted = fundNumber(value);
      if (label) {
        const n = formatted !== null ? Number(value) : Number.NaN;
        const lead = label.charAt(0);
        const cls = n > 0 || (Number.isNaN(n) && lead === '+') ? 'mf-pos'
          : n < 0 || (Number.isNaN(n) && lead === '-') ? 'mf-neg' : '';
        return { text: label, cls };
      }
      if (formatted !== null) {
        const n = Number(value);
        if (n > 0) return { text: '+' + formatted, cls: 'mf-pos' };
        if (n < 0) return { text: formatted, cls: 'mf-neg' };
        return { text: formatted, cls: '' };
      }
      const s = asText(value);
      if (!s) return null;
      if (s.charAt(0) === '+') return { text: s, cls: 'mf-pos' };
      if (s.charAt(0) === '-') return { text: s, cls: 'mf-neg' };
      return { text: s, cls: '' };
    },
    policyParagraphs() {
      return asList(this.cardData.policyParagraphs).map(asText).filter(Boolean);
    },
    policyBullets() {
      return asList(this.cardData.policyBullets).map(asText).filter(Boolean);
    },
    stats() {
      return asList(this.cardData.stats).map((s) => asObj(s) || {});
    },
    holdings() {
      return asList(this.cardData.holdings).map((h) => asObj(h) || {});
    },
    footer() {
      return asObj(this.cardData.footer);
    },
    /** The factsheet footer's single link, only if its scheme is one we stand behind (rule 4). */
    footerUrl() {
      if (!this.footer) return '';
      const url = asText(this.footer.linkUrl);
      return SAFE_URL.test(url) ? url : '';
    },

    /**
     * The comparison footer's link list, each carrying the separator that FOLLOWS it.
     *
     * The payload spells the punctuation out (`separator: ', '`, `lastSeparator: ' หรือ '`)
     * because the join is copy, not layout: "A, B, C หรือ D" is a sentence the author wrote.
     * `lastSeparator` falls back to `separator` — a card that authored only one of them means
     * one separator everywhere, not a missing word before the last item.
     */
    footerLinks() {
      const footer = this.footer;
      if (!footer) return [];
      const links = asList(footer.links).map((l) => asObj(l) || {});
      if (!links.length) return [];
      const sep = typeof footer.separator === 'string' ? footer.separator : ', ';
      const last = typeof footer.lastSeparator === 'string' ? footer.lastSeparator : sep;
      return links.map((link, i) => {
        const url = asText(link.url);
        let following = '';
        if (i < links.length - 1) following = i === links.length - 2 ? last : sep;
        return {
          text: asText(link.label) || url || EM_DASH,
          url: SAFE_URL.test(url) ? url : '',
          style: textStyle(link, 'label'),
          sep: following,
        };
      });
    },

    /**
     * The grid behind BOTH table cards, normalised so one block of markup can draw either.
     *
     * The two payloads differ only in three key names — `rowHeaderLabel`/`periodHeaderLabel`
     * for the corner cell, `funds`/`columns` for the header list, `label`/`period` for each
     * row's own header (and therefore for its `*Color`/`*FontSize` prefix) — so the shapes
     * are reconciled HERE, once, rather than by keeping two near-identical templates in sync.
     *
     * 🔴 THE COLUMN COUNT IS THE WIDEST THING IN THE PAYLOAD, not the header list's length.
     * A row short of cells is padded with blanks (a column this row has no answer for), and
     * a row with MORE cells than there are headers widens the table and leaves the extra
     * headers empty. Both are honest; truncating to `columns.length` would silently drop
     * data the author can see in their own JSON, which is the one thing a preview must not do.
     */
    tableModel() {
      const kind = this.kind;
      if (kind !== 'comparison' && kind !== 'performance') return null;

      const data = this.cardData;
      const isComparison = kind === 'comparison';
      const headKey = isComparison ? 'rowHeaderLabel' : 'periodHeaderLabel';
      const columnsKey = isComparison ? 'funds' : 'columns';
      const rowHeadKey = isComparison ? 'label' : 'period';

      const headerStyle = textStyle(data, columnsKey);
      const headers = asList(data[columnsKey]);
      const rows = asList(data.rows).map((r) => asObj(r) || {});

      let count = headers.length;
      rows.forEach((r) => {
        const cells = asList(r.cells);
        if (cells.length > count) count = cells.length;
      });

      const columns = [];
      for (let i = 0; i < count; i += 1) {
        columns.push({ text: asText(headers[i]), style: headerStyle });
      }

      const modelRows = rows.map((row) => {
        const rowFrame = fillStyle(row, 'rowFrameColor');
        const cells = asList(row.cells);
        const out = [];
        for (let i = 0; i < count; i += 1) {
          const cell = cells[i];
          const cellObj = asObj(cell);
          if (cellObj) {
            out.push({
              text: asText(cellObj.value) || EM_DASH,
              style: mergeStyles(
                rowFrame,
                fillStyle(cellObj, 'rowFrameColor'),
                this.valueStyle(cellObj, 'value', cellObj.tone),
              ),
              toneClass: this.toneClass(cellObj.tone),
            });
          } else {
            // A padded column, or a cell authored as a bare scalar instead of `{ value }`.
            // Blank — NOT an em dash: the dash means "this value is unknown", and a column
            // this row never had is a different statement.
            out.push({ text: asText(cell), style: rowFrame, toneClass: '' });
          }
        }
        return {
          headText: asText(row[rowHeadKey]) || EM_DASH,
          headStyle: mergeStyles(rowFrame, textStyle(row, rowHeadKey)),
          cells: out,
        };
      });

      // `tableHeight` is the card's own scroll window in px. Anything non-positive or
      // unreadable means "no ceiling" — never a 0-high box the admin cannot see into.
      const height = Number(data.tableHeight);
      const wrapStyle = Number.isFinite(height) && height > 0 ? { maxHeight: height + 'px' } : null;

      return {
        headLabel: asText(data[headKey]),
        headStyle: textStyle(data, headKey),
        columns,
        rows: modelRows,
        wrapStyle,
      };
    },

    /**
     * The fund picker (`type: 'MF'`, refId `type14`) — the one card the customer ANSWERS.
     *
     * 🔴 IT IS A FLAT ENVELOPE. `prefix` / `join` / `submit` / `list` / `cp` sit on the CARD,
     * not under `data`, so everything here reads `cardObj` — `cardData` is `{}` for this type
     * and every field would silently come back blank if it were read the usual way.
     *
     * 🔴 THE ECHO IS THE POINT. What the phone actually sends is `prefix + code` (or
     * `prefix + codes.join(join)`), and the two mistakes that shape can hide are both invisible
     * in a JSON textarea: a `prefix` whose trailing SPACE was trimmed away, and an empty `join`
     * that runs three codes together into one word. Drawing the resulting sentence, in a
     * whitespace-preserving box, is the only way an admin can see either before a customer does.
     *
     * 🔴 TWO STORED CONFIGS, ONE PER MODE, and `cp` says which one this is: `false` draws the
     * single-select card (a tap sends `prefix + code`; no button, so `join` and `submit` are
     * deliberately blank) and `true` draws the compare card (tick several, then the button sends
     * `prefix + codes.join(join)`). The node picks between them by node key —
     * `mutualFundPickerConfigCode` and `mutualFundPickerCompareConfigCode` — so nothing here is
     * decided at run time, and every check below is made against the mode this document declares.
     */
    picker() {
      if (this.kind !== 'picker') return null;
      const card = this.cardObj;

      const prefix = langSeat(card.prefix);
      const submit = langSeat(card.submit);
      // `join` is punctuation, never prose, so it is a plain string on both sides of the wire —
      // and it is taken VERBATIM: the space in ', ' is half of what it does.
      const join = typeof card.join === 'string' ? card.join : '';

      // 🔴 `cp` IS THE CARD'S IDENTITY, and it is a literal on both stored configs — the node carries
      //    one code per mode (`mutualFundPickerConfigCode` / `…CompareConfigCode`), so nothing decides
      //    it at run time. A string is tolerated (an admin who typed `"true"`) but called out below:
      //    the app dispatches on a real boolean.
      const compare = card.cp === true
        || (typeof card.cp === 'string' && card.cp.trim().toLowerCase() === 'true');

      // The list is either the finished array (a card someone pinned by hand) or the `@repeat`
      // directive a template carries. A directive cannot be rendered, only described — so it is
      // drawn with placeholders, clearly marked, rather than as an empty card.
      const directive = asObj(card.list);
      const repeatFrom = directive && typeof directive['@repeat'] === 'string'
        ? directive['@repeat'].trim()
        : '';
      let items = [];
      if (Array.isArray(card.list)) {
        // A bare scalar per entry, or `{ '@omitWhenNull', '@value' }` written inline.
        items = card.list.map((entry) => {
          const obj = asObj(entry);
          return (obj ? asText(obj['@value']) : asText(entry)) || EM_DASH;
        });
      } else if (repeatFrom) {
        items = PICKER_PLACEHOLDERS.slice();
      }

      // Every way this card can disappoint a customer while still looking fine in the editor.
      //
      // 🔴 EVERY CHECK IS PER MODE. `join` and `submit` are DELIBERATELY EMPTY on the single-select
      //    config — one code joins nothing, and a tap sends immediately so there is no button to
      //    caption — and a preview that flagged them would be teaching the admin to break the card.
      //    They are only worth complaining about on the compare config, where they are load-bearing.
      const warnings = [];
      const missing = prefix.missing.map((l) => 'prefix.' + l)
        .concat(compare ? submit.missing.map((l) => 'submit.' + l) : []);
      if (missing.length) {
        warnings.push('ถุงภาษาไม่ครบ (' + missing.join(', ') + ') — ขาดภาษาเดียวการ์ดทั้งใบเด้งกลับเป็นไทย');
      }
      if (typeof card.cp !== 'boolean') {
        warnings.push('cp ต้องเป็น true/false จริง ๆ (ไม่ใช่ข้อความหรือ placeholder) — '
          + 'แอปแยกสองโหมดของการ์ดนี้ด้วยค่านั้นค่าเดียว');
      }
      if (prefix.text === '') {
        warnings.push('prefix ว่าง — ข้อความที่ส่งกลับจะเหลือแต่รหัสกองทุนลอย ๆ');
      } else if (prefix.text.indexOf('{{') < 0 && prefix.text === prefix.text.replace(/\s+$/, '')) {
        warnings.push('prefix ไม่มีช่องว่างปิดท้าย — รหัสกองจะติดกับคำสุดท้ายเป็นคำเดียวกัน');
      }
      if (compare) {
        if (submit.text === '') {
          warnings.push('submit ว่าง — ใบเปรียบเทียบจะได้ปุ่มเปล่าไม่มีข้อความ');
        }
        if (join === '') {
          warnings.push('join ว่าง — รหัสกองที่เลือกจะถูกต่อกันเป็นก้อนเดียวโดยไม่มีตัวคั่น');
        }
      } else if (submit.text !== '' || join !== '') {
        // The exact shape of "the compare config was duplicated and `cp` flipped to false".
        warnings.push('cp = false แต่ยังมี join/submit ค้างอยู่ — ใบเลือกเดี่ยวส่งรหัสเดียวและไม่มีปุ่ม '
          + 'ทั้งสองค่านี้จึงไม่ถูกใช้ (ตั้งใจให้เป็นค่าว่าง)');
      }

      return {
        compare,
        // The colour / shape hints (`main*` / `item*`) — on the card, like everything else here (#925).
        frameStyle: pickerFrameStyle(card),
        listStyle: pickerListStyle(card),
        itemStyle: pickerItemStyle(card),
        itemShapeStyle: pickerItemShapeStyle(card),
        arrowStyle: pickerArrowStyle(card),
        modeLabel: compare
          ? 'cp = true · ใบเปรียบเทียบ — ติ๊กได้หลายกอง แล้วกดปุ่มส่ง'
          : 'cp = false · ใบเลือกเดี่ยว — แตะกองเดียวแล้วส่งกลับทันที ไม่มีปุ่ม',
        prefixText: prefix.text,
        submitText: submit.text,
        // Only the seats this mode actually reads: listing the single-select's blank `join`/`submit`
        // would invite an admin to "fix" two values the phone will never look at.
        seats: [{ key: 'prefix', value: prefix.text }].concat(
          compare ? [{ key: 'join', value: join }, { key: 'submit', value: submit.text }] : [],
        ),
        join,
        items,
        repeatFrom,
        // The sentence this config sends: one code, or every ticked code joined. There is only ever
        // one of them, because there is only ever one mode per stored config.
        echo: items.length
          ? prefix.text + (compare ? items.join(join) : items[0])
          : '',
        warnings,
      };
    },
  },
  watch: {
    /** A new logo URL deserves a fresh chance to load. */
    iconUrl() {
      this.iconBroken = false;
    },
  },
  methods: {
    /** One scalar as display text — see `asText`. */
    text: asText,
    /** `<prefix>Color` + `<prefix>FontSize` on `src`, as an inline style (or null). */
    ts: textStyle,
    /** The class that paints a toned figure, or '' (rule 3). */
    toneClass(tone) {
      const key = asText(tone).toUpperCase();
      return TONE_CLASS[key] || '';
    },
    /**
     * A figure's inline style, with the authored colour REMOVED when a tone is going to
     * paint it (rule 3). Font size and alignment still come from the payload.
     */
    valueStyle(src, prefix, tone) {
      const style = textStyle(src, prefix);
      if (!style || !this.toneClass(tone)) return style;
      const rest = Object.assign({}, style);
      delete rest.color;
      return Object.keys(rest).length ? rest : null;
    },
    /**
     * A holding's share of the fund. A number gets its '%' added; a string that already
     * carries one is left exactly as the author wrote it.
     */
    percent(value) {
      const formatted = fundNumber(value);
      if (formatted !== null) return formatted + '%';
      const s = asText(value);
      if (!s) return EM_DASH;
      return s.charAt(s.length - 1) === '%' ? s : s + '%';
    },
  },
};
</script>

<style scoped>
/* Every colour below is a FALLBACK: a payload that authored the matching `*Color` /
   `*FontSize` / `rowFrameColor` overrules it through an inline style. */
.mf-card-preview {
  font-size: 14px;
  line-height: 1.55;
  color: #1f2a44;
}

.mf-frame {
  background: #ffffff;
  border: 1px solid #e3e7ee;
  border-radius: 16px;
  padding: 16px;
}

/* ── head ─────────────────────────────────────────────────────────────────────────── */
.mf-head {
  display: flex;
  align-items: center;
  gap: 10px;
}

.mf-icon {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  object-fit: contain;
  flex: 0 0 auto;
}

.mf-head-text {
  min-width: 0;
}

.mf-symbol {
  font-weight: 700;
  font-size: 16px;
}

.mf-fund-name {
  color: #5a6270;
}

/* ── NAV ──────────────────────────────────────────────────────────────────────────── */
.mf-nav {
  background: #f5f7fa;
  border-radius: 12px;
  padding: 10px 12px;
  margin: 12px 0;
}

.mf-nav-line {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 6px;
}

.mf-nav-label {
  color: #5a6270;
}

.mf-nav-value {
  font-weight: 700;
  font-size: 20px;
}

.mf-nav-unit {
  color: #5a6270;
}

.mf-nav-change {
  font-weight: 600;
}

/* ── prose ────────────────────────────────────────────────────────────────────────── */
.mf-title {
  font-weight: 700;
  font-size: 16px;
  margin-bottom: 10px;
}

.mf-section-title {
  font-weight: 700;
  font-size: 16px;
  margin: 14px 0 6px;
}

.mf-para {
  margin: 0 0 8px;
}

.mf-bullets {
  margin: 0 0 8px;
  padding-left: 20px;
}

.mf-bullets li {
  margin-bottom: 4px;
}

.mf-caption {
  color: #8a8f9c;
  font-size: 12px;
  margin-top: 6px;
}

.mf-disclaimer {
  color: #8a8f9c;
  font-size: 12px;
  margin-top: 10px;
}

/* ── label/value rows (stats, holdings) ───────────────────────────────────────────── */
.mf-rows {
  margin-top: 6px;
}

.mf-row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 16px;
  padding: 6px 0;
  border-bottom: 1px dashed #edf0f5;
}

.mf-row:last-child {
  border-bottom: none;
}

.mf-row-label {
  min-width: 0;
}

.mf-row-value {
  text-align: right;
  white-space: nowrap;
}

/* The two colours `tone` may paint — the same pair `TaxSummaryCardV2` uses. */
.mf-pos {
  color: #1a9259;
}

.mf-neg {
  color: #b64a35;
}

/* ── table (comparison + performance) ─────────────────────────────────────────────── */
.mf-table-wrap {
  overflow: auto;
  border: 1px solid #e3e7ee;
  border-radius: 12px;
  background: #ffffff;
}

.mf-table {
  border-collapse: separate;
  border-spacing: 0;
  width: 100%;
}

.mf-th,
.mf-td {
  padding: 8px 12px;
  text-align: left;
  white-space: nowrap;
  border-bottom: 1px solid #edf0f5;
}

.mf-th {
  font-weight: 700;
  background: #f5f7fa;
  position: sticky;
  top: 0;
  z-index: 1;
}

.mf-rowhead {
  font-weight: 600;
  text-align: left;
}

/* 🔴 The frozen first column. `background` is not decoration here: without an OPAQUE
   background the scrolled columns slide visibly underneath it and the freeze looks
   broken. A row that authored `rowFrameColor` paints over this through its inline style,
   which is why the fallback has to be opaque too — a transparent default would leak. */
.mf-freeze {
  position: sticky;
  left: 0;
  z-index: 2;
  background: #ffffff;
  box-shadow: 1px 0 0 #e3e7ee;
}

/* The corner cell is frozen in BOTH directions, so it has to sit above both. */
.mf-th.mf-freeze {
  z-index: 3;
  background: #f5f7fa;
}

/* ── picker (MF / type14) ─────────────────────────────────────────────────────────── */
/* Deliberately NOT a copy of the phone's chip list. This is an authoring preview, and its
   job is to answer "did I configure it right", so the mode it is in and the sentence it
   sends are given more room than the pretty part. */
.mf-picker-mode {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #5a6270;
  font-size: 12px;
  margin-bottom: 10px;
}

.mf-picker-mode-icon {
  flex: 0 0 auto;
}

.mf-picker-seat {
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin-bottom: 2px;
}

.mf-picker-seat-key {
  flex: 0 0 52px;
  color: #8a8f9c;
  font-size: 12px;
}

/* `min-width: 0` or a long placeholder would push the row wider than the card instead of wrapping. */
.mf-picker-seat .mf-picker-echo-line {
  flex: 1 1 auto;
  min-width: 0;
}

/* The stack, with the gap the mockup breathes at — the pills are separate objects, not rows of one
   list, so the spacing between them is part of the shape rather than a border. */
.mf-picker-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

/* ONE FUND = ONE PILL. Full-round (999px, not a rounded rect), full width of whatever box the
   preview is sitting in — which is ~300px here, not a phone — so the text has to be allowed to
   shorten rather than push the pill wider than the card. */
.mf-pill {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 11px 18px;
  border-radius: 999px;
  background: linear-gradient(90deg, #eaf5e3 0%, #d7ecce 100%);
  box-shadow: 0 2px 6px rgba(31, 42, 68, 0.1);
  color: #17203a;
  font-weight: 700;
}

.mf-pill-text {
  flex: 1 1 auto;
  /* `min-width: 0` is what actually lets the ellipsis happen: without it a flex item refuses to
     shrink below its content and the pill grows past the card instead. */
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* ► as a text glyph rather than a CSS triangle or an icon font: it is one character, it inherits
   `color` from the pill (so the submit pill's white arrow needs no second rule beyond the colour),
   and it cannot fail to load the way an icon font can. */
.mf-pill-arrow {
  flex: 0 0 auto;
  font-size: 11px;
  line-height: 1;
  color: #4a5568;
}

/* The submit pill: same shape, same width, deliberately DARKER so it reads as the end of the list
   rather than one more fund. Declared after `.mf-pill` so the later rule wins on equal specificity. */
.mf-pill-submit {
  margin-top: 10px;
  background: linear-gradient(90deg, #7d9384 0%, #64806f 100%);
  color: #ffffff;
}

.mf-pill-submit .mf-pill-arrow {
  color: #ffffff;
}

.mf-picker-echo {
  margin-top: 14px;
  padding: 10px 12px;
  background: #f5f7fa;
  border: 1px solid #e3e7ee;
  border-radius: 12px;
}

.mf-picker-echo-title {
  color: #5a6270;
  font-size: 12px;
  font-weight: 600;
  margin-bottom: 6px;
}

.mf-picker-echo-label {
  color: #8a8f9c;
  font-size: 12px;
  margin-top: 6px;
}

/* 🔴 `pre-wrap` is the whole reason this box exists: `prefix` ends in a space the phone
   concatenates the fund code onto, and normal HTML whitespace collapsing would hide both
   its presence and its absence. */
.mf-picker-echo-line {
  white-space: pre-wrap;
  word-break: break-word;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 12px;
  line-height: 1.6;
}

/* ── footer ───────────────────────────────────────────────────────────────────────── */
.mf-footer {
  margin-top: 12px;
  padding-top: 10px;
  border-top: 1px solid #edf0f5;
}

/* 🔴 The gap between the footer's copy and its first link is a MARGIN, not a space in the
   markup: Vue's whitespace: 'condense' deletes the newline between two elements, so the
   "คลิก" and the link would print welded together ("คลิกONE-ELITE11RA"). The separators
   BETWEEN links are different — they are characters the payload authored (`separator`,
   `lastSeparator`) and carry their own spaces. */
.mf-footer-label {
  margin-inline-end: 4px;
}

.mf-footer-link {
  white-space: nowrap;
}

.mf-link {
  color: #1867c0;
  text-decoration: underline;
}

.mf-sep {
  color: #5a6270;
}

/* ── empty / unsupported ──────────────────────────────────────────────────────────── */
.mf-empty {
  color: #8a8f9c;
  font-size: 13px;
  padding: 24px 12px;
  text-align: center;
  border: 1px dashed #d7dce5;
  border-radius: 12px;
}

.mf-raw {
  margin: 0;
  padding: 10px 12px;
  background: #f5f7fa;
  border: 1px solid #e3e7ee;
  border-radius: 8px;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 12px;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
  max-height: 420px;
  overflow: auto;
}
</style>
